<!--
// app/components/pages/region-world-cup/KwcTablesAllLeagues.vue
-->
<script setup lang="ts">
import TableKwccProbability from "~/components/pages/region-tables-kwcc/TableKwccProbability.vue";
import SliderWrapper from "~/components/Shared/SliderWrapper.vue";

type Props = {
  isHome?: boolean;
  isTournament?: boolean;
};

const props = defineProps<Props>();

const { $t } = useI18n();

const { data, error } = await useFetch("/api/v1/competition/kwcc-tables", {
  query: {
    spain: 54,
    americas: 55,
    italy: 58,
    brazil: 61,
    france: 63,
    germany: 59,
  },
});
if (error.value) console.error(error.value);

const numberOfLeagues: number = 7;
</script>

<template>
  <section v-if="data" :class="['py-6 xl:px-0 grid grid-cols-1', { 'kql-w-container': !isTournament }]">
    <div
      :class="[
        { 'mt-8 md:p-4 rounded-lg lg:mt-0': !isTournament },
        { 'md:bg-gray-900': isHome && !isTournament },
      ]"
    >
      <div :class="{ 'px-4 md:px-0': !isTournament }">
        <div
          v-if="isHome && !isTournament"
          class="flex text-gray-400 uppercase text-sm mb-2 justify-between items-end"
        >
          <p>Road to KWC Clubs 2026</p>
          <i18n-link
            :to="{
              name: `region-tables`,
              params: { region: 'world-cup' },
            }"
            class="normal-case text-primary text-xs"
          >
            {{ $t("cards.stats.seeMore.link") }}
          </i18n-link>
        </div>
        <hr v-if="isHome && !isTournament" />
        <slider-wrapper :scroll-distance="220" scrollable-selector="#tablesSlider">
          <div id="tablesSlider" class="max-w-full overflow-x-scroll hide-scrollbar">
            <div
              :class="[
                'grid grid-flow-col gap-x-2 min-w-max',
                isHome && !isTournament
                  ? 'md:gap-x-0 mt-4 justify-stretch'
                  : isTournament
                    ? 'px-2'
                    : 'md:gap-x-4',
              ]"
            >
              <div v-if="data.spain && numberOfLeagues >= 1">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Spain</component
                >
                <table-kwcc-probability
                  :table-data="data.spain.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  :extra-classified-teams="2"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
              <div v-if="data.americas && numberOfLeagues >= 2">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Openbank Mexico</component
                >
                <table-kwcc-probability
                  :table-data="data.americas.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  :extra-classified-teams="1"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
              <div v-if="data.italy && numberOfLeagues >= 3">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Lottomatica.sport Italy</component
                >
                <table-kwcc-probability
                  :table-data="data.italy.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  :extra-classified-teams="2"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
              <div v-if="data.brazil && numberOfLeagues >= 4">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Brazil</component
                >
                <table-kwcc-probability
                  :table-data="data.brazil.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  :extra-classified-teams="1"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
              <div v-if="data.france && numberOfLeagues >= 5">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Crédit Agricole France</component
                >
                <table-kwcc-probability
                  :table-data="data.france.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
              <div v-if="data.germany && numberOfLeagues >= 6">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Germany</component
                >
                <table-kwcc-probability
                  :table-data="data.germany.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
              <div v-if="data.mena && numberOfLeagues >= 7">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League MENA</component
                >
                <table-kwcc-probability
                  :table-data="data.mena.data"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                  is-victories-defeats
                  no-points
                ></table-kwcc-probability>
              </div>
            </div>
          </div>
        </slider-wrapper>

        <div v-if="!isHome" class="flex flex-wrap gap-x-8 gap-y-4 justify-end">
          <div class="flex items-center gap-x-3">
            <div class="w-1.5 h-6 !bg-red-500"></div>
            <div class="text-xl !text-red-500">
              {{ $t(`tables.legend.placement.qualified`) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
#tablesSlider div {
  width: 305px;
}
</style>
